import {
    ColumnDef,
    getExpandedRowModel,
} from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import DataTableTemplate from "Components/templates/data-table-template"
import SearchBillabilityForm from "Components/organisms/forms/search-billability-form"
import AreYouSureModal from "Components/organisms/are-you-sure-modal"
import CheckboxInput from "Components/molecule/inputs/checkbox-input"
import SyncButton from "Components/molecule/sync-button"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import { PencilIcon, TrashIcon, CheckCircleIcon, HelpIcon, ChartIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"
import useSyncProcess from "hooks/useSyncProcess"
import Modal from "Components/molecule/modal"
import DataTable from "Components/molecule/data-table"
import BillabilityReportRecipientForm from "Components/organisms/forms/billability-report-recipient-form"
import BillabilityReportRecipientFields from "Components/organisms/forms/billability-report-recipient-form/types"
import { BillabilityReportRecipientFormValidation } from "data/billability-report-recipient-form"
import { BillabilityReportRecipient } from "models/billability-report-recipient"
import Employee from "models/employee"
import { FormikHelpers, useFormik } from "formik"
import BillabilityTrendChart from "Components/molecule/charts/billability-trend-chart"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { createSelector } from "reselect"
import { BillabilitySliceState, setBillabilityQuery, setRows, setTrend, setTrendByGroup, setReportRecipients, addReportRecipient, setReportRecipient, removeReportRecipient } from "slices/finance/billability/reducer"
import {
    downloadBillabilityCSV, getAllBillability, getBillabilitySearchFields, syncBillabilityReport,
    getBillabilityReportRecipients, getBillabilityReportRecipientFormFields, createBillabilityReportRecipient,
    updateBillabilityReportRecipient, deleteBillabilityReportRecipient
} from "slices/finance/billability/thunk"
import { IBillabilityQuery, IBillabilityRow, ITrendTarget, BillabilityAndUtilizationUI, DEFAULT_BILLABILITY_QUERY } from "models/billability"
import { DepartmentLevelDepth } from "models/department-level"
import { SyncLogTypes } from "models/sync-log"
import { SCREENS } from "models/screen"
import { PermissionsSliceState } from "slices/auth/permissions/reducer"
import { SyncLogSliceState } from "slices/sync-log/reducer"
import { getMostRecentSyncLog } from "slices/thunk"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { setLocale } from "yup"
import { toDepartmentOptions } from "utils/department-options"

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({ screens })
)

const selectSyncLog = createSelector(
    (state: any) => state.SyncLog,
    ({ syncLogs }: SyncLogSliceState) => ({
        syncLogs
    })
)

const selectBillability = createSelector(
    (state: any) => state.Billability,
    ({
        query,
        rows,
        trend,
        trendByGroup,
        employeeClass,
        departments,
        branches,
        departmentLevels,
        reportRecipients
    }: BillabilitySliceState) => ({
        query,
        rows,
        trend,
        trendByGroup,
        employeeClass,
        departments,
        branches,
        departmentLevels,
        reportRecipients
    })
)

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)
const { fiscalYear } = calendar.dateToFiscal(new Date())

// The Group By select carries one value, but the query needs two fields. Department
// levels are identified by their id; 'branch'/'employee' are fixed sentinels.
const BRANCH_OPTION = 'branch'
const EMPLOYEE_OPTION = 'employee'

const groupBySelection = (query: IBillabilityQuery): string => {
    if (query.groupBy === 'branch') return BRANCH_OPTION
    if (query.groupBy === 'employee') return EMPLOYEE_OPTION
    return query.departmentLevelId ?? ''
}

const fmtHr = (value: number) =>
    value.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const BillabilityPage = () => {
    document.title = "Billability / Utilization | IbexIQ"
    const { handleOpenManual } = useManual("finance/billability");

    const dispatch = useDispatch() as any
    const { query, rows, trend, trendByGroup, employeeClass, departments, branches, departmentLevels, reportRecipients } = useSelector(selectBillability)
    const [searchParams] = useSearchParams()

    const { syncLogs } = useSelector(selectSyncLog)
    const { screens } = useSelector(selectPermissions)

    const isAdmin = useMemo(() => screens.map((s) => s.code).some((c) => c === SCREENS.ADMIN), [screens])

    // Rows are cached in the slice, so a return visit only shows the loader when there's nothing to show yet.
    const [isLoading, setIsLoading] = useState(rows.length === 0)
    const [isExporting, setIsExporting] = useState(false)
    const [showSyncModal, setShowSyncModal] = useState(false)
    const [showChart, setShowChart] = useState(true)
    const [showExtraColumns, setShowExtraColumns] = useState(false)

    const employeeClassOptions = useMemo(
        () => employeeClass.map(ec => ({ value: ec.id, label: ec.id })),
        [employeeClass]
    )

    const departmentOptions = useMemo(
        () => toDepartmentOptions(departments),
        [departments]
    )

    const branchOptions = useMemo(
        () => branches.map(b => ({ value: b.id, label: b.name })),
        [branches]
    )

    // Levels come from the DepartmentLevel table (shallowest already stripped server-side),
    // so adding a level in admin makes it groupable here without a code change.
    const groupByOptions = useMemo(() => [
        ...departmentLevels.map(l => ({ value: l.id, label: l.description })),
        { value: BRANCH_OPTION, label: 'Branch' },
        { value: EMPLOYEE_OPTION, label: 'Employee' },
    ], [departmentLevels])

    // The API resolves each period's fiscal-year target onto the trend; pull it off so
    // the chart gets the benchmark without re-deriving fiscal years client-side.
    const trendTargets = useMemo<ITrendTarget[]>(
        () => trend.map(t => ({
            fiscalPeriod: t.fiscalPeriod,
            utilizationTarget: t.utilizationTarget,
            actualBillableTarget: t.actualBillableTarget,
        })),
        [trend]
    )

    const handleChangeGroupBy = (value: string) => {
        if (value === BRANCH_OPTION || value === EMPLOYEE_OPTION) {
            dispatch(setBillabilityQuery({ ...query, groupBy: value, departmentLevelId: undefined }))
            return
        }
        dispatch(setBillabilityQuery({ ...query, groupBy: 'departmentLevel', departmentLevelId: value }))
    }

    const handleChangeQuery = (key: string, value: any) => {
        dispatch(setBillabilityQuery({ ...query, [key]: value }))
    }

    const handleExportData = () => {
        setIsExporting(true)
        try {
            const selection = groupBySelection(query)
            const groupByLabel = groupByOptions.find(o => o.value === selection)?.label ?? 'Employee'
            // The table's own rows, already sorted and flattened in render order.
            const displayRows = table.getRowModel().rows.map(r => r.original)
            downloadBillabilityCSV(displayRows, query, departments, branches, groupByLabel)
        } finally {
            setIsExporting(false)
        }
    }

    const handleSearch = async () => {
        setIsLoading(true)
        const result = await dispatch(getAllBillability(query))
        if (result) {
            dispatch(setRows(result.data))
            dispatch(setTrend(result.trend))
            dispatch(setTrendByGroup(result.trendByGroup))
        }
        setIsLoading(false)
    }

    const handleClearSearch = async () => {
        dispatch(setBillabilityQuery(DEFAULT_BILLABILITY_QUERY))
        setIsLoading(true)
        const result = await dispatch(getAllBillability(DEFAULT_BILLABILITY_QUERY))
        if (result) {
            dispatch(setRows(result.data))
            dispatch(setTrend(result.trend))
            dispatch(setTrendByGroup(result.trendByGroup))
        }
        setIsLoading(false)
    }


    const { isSyncing, startSync } = useSyncProcess({
        module: SyncLogTypes.BILLABILITY_REPORT,
        start: () => syncBillabilityReport(fiscalYear.toString()),
        onFinished: handleSearch,
    })

    const handleSync = async () => {
        setShowSyncModal(false)
        await startSync()
    }

    const initializePage = async () => {
        // A link from the billability report email carries its period: show what the email
        // showed (those dates, every employee class, one row per employee), not the cached query.
        const fromDate = searchParams.get('fromDate')
        const toDate = searchParams.get('toDate')
        const linkQuery = fromDate && toDate
            ? { ...DEFAULT_BILLABILITY_QUERY, fromDate, toDate, employeeClassIds: undefined, billable: ['true'], groupBy: 'employee' as const }
            : null
        if (linkQuery) dispatch(setBillabilityQuery(linkQuery))

        const prs: any = []
        if (employeeClass.length === 0) {
            prs.push(dispatch(getBillabilitySearchFields()))
        }

        if (!syncLogs[SyncLogTypes.BILLABILITY_REPORT]) {
            prs.push(dispatch(getMostRecentSyncLog(SyncLogTypes.BILLABILITY_REPORT)))
        }

        // Uses the stored query so the cached rows always match what the search form shows.
        if (rows.length === 0 || linkQuery)
            prs.push(dispatch(getAllBillability(linkQuery ?? query)).then((result: any) => {
                if (result) {
                    dispatch(setRows(result.data))
                    dispatch(setTrend(result.trend))
                    dispatch(setTrendByGroup(result.trendByGroup))
                }
            }))

        await Promise.all(prs);
        setIsLoading(false)

    }

    useEffect(() => {
        initializePage();
    }, [])

    // DEFAULT_BILLABILITY_QUERY omits departmentLevelId so the server falls back to Division. Once the
    // levels arrive, name that level explicitly so the dropdown shows what the results are
    // actually grouped by instead of sitting blank. Must stay in step with the server's
    // fallback, or the dropdown will claim one grouping while the data shows another.
    useEffect(() => {
        if (departmentLevels.length === 0) return
        if (query.groupBy !== 'departmentLevel' || query.departmentLevelId) return
        const fallback = departmentLevels.find(l => l.depth === DepartmentLevelDepth.Division)
            ?? departmentLevels.reduce((a, b) => (b.depth > a.depth ? b : a))
        dispatch(setBillabilityQuery({ ...query, departmentLevelId: fallback.id }))
    }, [departmentLevels, query])

    // Group header rows are the ones carrying nested employees.
    const groupClass = (row: IBillabilityRow) => row.nested ? 'fw-bold table-secondary' : BillabilityAndUtilizationUI.billabilityPercentColor(row.actualBillablePct)

    const columns = useMemo<ColumnDef<IBillabilityRow>[]>(() => [
        {
            header: "Name",
            accessorKey: "employeeName",
            size: 220,
            meta: { getCellClassName: groupClass },
            cell: ({ row }) => (
                // Group rows: the whole name (arrow + text) collapses/expands the group
                <span
                    onClick={row.getCanExpand() ? row.getToggleExpandedHandler() : undefined}
                    role={row.getCanExpand() ? 'button' : undefined}
                    title={row.getCanExpand() ? (row.getIsExpanded() ? 'Collapse' : 'Expand') : undefined}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: `${row.depth * 16}px`, cursor: row.getCanExpand() ? 'pointer' : undefined }}
                >
                    {row.getCanExpand() && <span aria-hidden>{row.getIsExpanded() ? '▾' : '▸'}</span>}
                    <span>{row.original.employeeName}</span>
                </span>
            ),
        },
        {
            header: "Department",
            accessorKey: "departmentName",
            size: 200,
            meta: { getCellClassName: groupClass },
        },
        {
            header: "Employee Class",
            accessorKey: "employeeClassId",
            size: 140,
            meta: { getCellClassName: groupClass },
        },
        {
            header: "Time Total (Hr)",
            accessorKey: "timeTotal",
            size: 140,
            meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
            cell: ({ row }) => fmtHr(row.original.timeTotal),
        },
        {
            header: "Time Billable (Hr)",
            accessorKey: "timeBillable",
            size: 140,
            meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
            cell: ({ row }) => fmtHr(row.original.timeBillable),
        },
        {
            header: "Other Non Billable (Hr)",
            accessorKey: "otherNonBillable",
            size: 160,
            meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
            cell: ({ row }) => fmtHr(row.original.otherNonBillable),
        },
        {
            header: "Total Non Billable (Hr)",
            id: "totalNonBillable",
            accessorFn: (row) => row.timeTotal - row.timeBillable,
            size: 160,
            meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
            cell: ({ row }) => fmtHr(row.original.timeTotal - row.original.timeBillable),
        },
        {
            header: "Billable (%)",
            accessorKey: "actualBillablePct",
            size: 140,
            meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
            cell: ({ row }) => `${fmtHr(row.original.actualBillablePct)}%`,
        },
        {
            header: "Utilization (%)",
            accessorKey: "utilizationPct",
            size: 120,
            meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
            cell: ({ row }) => `${fmtHr(row.original.utilizationPct)}%`,
        },
        ...(showExtraColumns ? [
            {
                header: "Branch",
                accessorKey: "branchName",
                size: 160,
                meta: { getCellClassName: groupClass },
            },
            {
                header: "Time Available (Hr)",
                accessorKey: "timeAvailable",
                size: 140,
                meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
                cell: ({ row }) => fmtHr(row.original.timeAvailable),
            },
            {
                header: "Bill Rate (%)",
                accessorKey: "billRatePct",
                size: 100,
                meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
                cell: ({ row }) => `${fmtHr(row.original.billRatePct)}%`,
            },
            {
                header: "Billable Target (Hr)",
                accessorKey: "billableTarget",
                size: 140,
                meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
                cell: ({ row }) => fmtHr(row.original.billableTarget),
            },
            {
                header: "Prospects Time Non Billable (Hr)",
                accessorKey: "prospectsTimeNonBillable",
                size: 160,
                meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
                cell: ({ row }) => fmtHr(row.original.prospectsTimeNonBillable),
            },
            {
                header: "RAM Time Non Billable (Hr)",
                accessorKey: "ramTimeNonBillable",
                size: 160,
                meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
                cell: ({ row }) => fmtHr(row.original.ramTimeNonBillable),
            },
            {
                header: "Non Project Time Non Billable (Hr)",
                id: "nonProjectTimeNonBillable",
                accessorFn: (row: IBillabilityRow) => row.projxTimeNonBillable - row.statTimeNonBillable,
                size: 160,
                meta: { getHeaderClassName: () => 'text-end', getCellClassName: (row: IBillabilityRow) => `text-end ${groupClass(row)}`.trim() },
                cell: ({ row }) => fmtHr(row.original.projxTimeNonBillable - row.original.statTimeNonBillable),
            },
        ] as ColumnDef<IBillabilityRow>[] : []),
    ], [showExtraColumns])

    // ── Report recipients (admin-only modal) ─────────────────────────────────
    const [showRecipientsModal, setShowRecipientsModal] = useState(false)
    const [isLoadingRecipients, setIsLoadingRecipients] = useState(false)
    const [recipientEmployees, setRecipientEmployees] = useState<Partial<Employee>[]>([])
    const [showRecipientFormModal, setShowRecipientFormModal] = useState(false)
    const [editingRecipient, setEditingRecipient] = useState<BillabilityReportRecipient | null>(null)
    const [deletingRecipient, setDeletingRecipient] = useState<BillabilityReportRecipient | null>(null)
    const [isDeletingRecipient, setIsDeletingRecipient] = useState(false)

    const handleOpenRecipients = async () => {
        setShowRecipientsModal(true)
        setIsLoadingRecipients(true)
        const [recipients, formFields] = await Promise.all([
            getBillabilityReportRecipients(),
            getBillabilityReportRecipientFormFields(),
        ])
        if (recipients) dispatch(setReportRecipients(recipients))
        if (formFields) setRecipientEmployees(formFields.employees)
        setIsLoadingRecipients(false)
    }

    const onSubmitRecipient = async (
        vals: BillabilityReportRecipientFields,
        helpers: FormikHelpers<BillabilityReportRecipientFields>
    ) => {
        helpers.setSubmitting(true)

        if (editingRecipient) {
            const res = await updateBillabilityReportRecipient(editingRecipient.id, vals.weekly, vals.monthly)
            if (res) {
                dispatch(setReportRecipient(res))
                handleCloseRecipientForm()
            }
        } else {
            const res = await createBillabilityReportRecipient(vals.employeeId, vals.weekly, vals.monthly)
            if (res) {
                dispatch(addReportRecipient(res))
                handleCloseRecipientForm()
            }
        }

        helpers.setSubmitting(false)
    }

    const recipientForm = useFormik<BillabilityReportRecipientFields>({
        initialValues: { employeeId: '', weekly: true, monthly: false },
        onSubmit: onSubmitRecipient,
        validationSchema: BillabilityReportRecipientFormValidation,
    })

    const handleCloseRecipientForm = () => {
        setShowRecipientFormModal(false)
        setEditingRecipient(null)
        recipientForm.resetForm()
    }

    const handleAddRecipient = () => {
        setEditingRecipient(null)
        recipientForm.resetForm()
        setShowRecipientFormModal(true)
    }

    const handleEditRecipient = (recipient: BillabilityReportRecipient) => {
        setEditingRecipient(recipient)
        recipientForm.setValues({ employeeId: recipient.employeeId, weekly: recipient.weekly, monthly: recipient.monthly }, false)
        setShowRecipientFormModal(true)
    }

    const confirmDeleteRecipient = async () => {
        if (!deletingRecipient) return
        setIsDeletingRecipient(true)
        const success = await deleteBillabilityReportRecipient(deletingRecipient.id)
        if (success) {
            dispatch(removeReportRecipient(deletingRecipient.id))
            setDeletingRecipient(null)
        }
        setIsDeletingRecipient(false)
    }

    // Active employees who aren't on the list yet.
    const recipientEmployeeOptions = useMemo(() => recipientEmployees
        .filter(e => !reportRecipients.some(r => r.employeeId === e.id))
        .map(e => ({ value: e.id!, label: `${e.commonName ?? e.firstName} ${e.lastName}` })),
        [recipientEmployees, reportRecipients])

    const recipientColumns = useMemo<ColumnDef<BillabilityReportRecipient>[]>(() => [
        {
            header: "Name",
            id: "name",
            accessorFn: (row) => `${row.employee.commonName ?? row.employee.firstName} ${row.employee.lastName}`,
            size: 240,
            cell: ({ row }) => `${row.original.employee.commonName ?? row.original.employee.firstName} ${row.original.employee.lastName}`,
        },
        {
            header: "Weekly",
            accessorKey: "weekly",
            size: 100,
            cell: ({ row }) => row.original.weekly && <CheckCircleIcon className="text-success" />,
        },
        {
            header: "Monthly",
            accessorKey: "monthly",
            size: 140,
            cell: ({ row }) => <TableRowControlsContainer style={{ justifyContent: 'space-between' }}>
                {row.original.monthly ? <CheckCircleIcon className="text-success" /> : <span />}
                <div className='d-flex flex-row justify-content-end'>
                    <IconButton onClick={() => handleEditRecipient(row.original)}><PencilIcon /></IconButton>
                    <IconButton onClick={() => setDeletingRecipient(row.original)}><TrashIcon /></IconButton>
                </div>
            </TableRowControlsContainer>
        },
    ], [])

    const recipientTable = useDataTable({
        columns: recipientColumns,
        data: reportRecipients,
    })

    // Header-click sorting; grouped rows are sorted within their group
    const table = useDataTable({
        data: rows,
        columns,
        getSubRows: row => row.nested,
        initialState: {
            expanded: true,
        },
        getExpandedRowModel: getExpandedRowModel(),
        columnResizeMode: 'onEnd',
        enableColumnResizing: true,
    })

    return (
        <DataTableTemplate
            fullExport={handleExportData}
            title="Finance"
            breadcrumbItem="Billability / Utilization"
            table={table}
            columns={columns}
            isLoading={isLoading}
            actions={<>
                {isAdmin && (<>
                    <SyncButton
                        id="billabilitySyncBtn"
                        isSyncing={isSyncing}
                        syncLog={syncLogs[SyncLogTypes.BILLABILITY_REPORT]}
                        onClick={() => setShowSyncModal(true)}
                    />
                    <ActionButton onClick={handleOpenRecipients}>Report Recipients</ActionButton>
                </>)}
                <ActionButton onClick={() => setShowChart(prev => !prev)}>
                    {showChart ? 'Hide Chart' : 'Show Chart'} <ChartIcon />
                </ActionButton>
                <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
            </>}
            modal={<>
                <AreYouSureModal
                    isOpen={showSyncModal}
                    onModalClose={() => setShowSyncModal(false)}
                    modalTitle="Sync Billability / Utilization"
                    subTitle={`Rebuilds the report for fiscal year ${fiscalYear}.`}
                    handleAreYouSureAccept={handleSync}
                    handleAreYouSureDecline={async () => setShowSyncModal(false)}
                    areYouSureacceptText={isSyncing ? 'Syncing...' : 'Sync'}
                    areYouSuredeclineText="Cancel"
                />
                <Modal
                    isOpen={showRecipientsModal}
                    onClose={() => setShowRecipientsModal(false)}
                    title="Report Recipients"
                    subtitle="Department heads receive the report emails automatically. Add anyone else here."
                    className="fit-content"
                >
                    <ActionButton className="mb-3" onClick={handleAddRecipient}>Add</ActionButton>
                    {!isLoadingRecipients && <DataTable table={recipientTable} columns={recipientColumns} />}
                </Modal>
                <Modal
                    isOpen={showRecipientFormModal}
                    onClose={handleCloseRecipientForm}
                    title={editingRecipient ? "Edit Recipient" : "Add Recipient"}
                >
                    <BillabilityReportRecipientForm
                        edit={!!editingRecipient}
                        employees={recipientEmployeeOptions}
                        values={recipientForm.values}
                        errors={recipientForm.errors}
                        touched={recipientForm.touched}
                        handleChange={recipientForm.handleChange}
                        handleBlur={recipientForm.handleBlur}
                        handleSubmit={recipientForm.handleSubmit}
                        setFieldValue={recipientForm.setFieldValue}
                        isValid={recipientForm.isValid}
                        isSubmitting={recipientForm.isSubmitting}
                    />
                </Modal>
                <AreYouSureModal
                    isOpen={!!deletingRecipient}
                    onModalClose={async () => setDeletingRecipient(null)}
                    modalTitle="Remove Recipient"
                    subTitle="Are you sure you want to remove this recipient"
                    handleAreYouSureAccept={confirmDeleteRecipient}
                    handleAreYouSureDecline={async () => setDeletingRecipient(null)}
                    areYouSureacceptText={isDeletingRecipient ? 'Removing...' : 'Remove'}
                    areYouSuredeclineText="Cancel"
                />
            </>}
            tableActions={
                <>
                    <CheckboxInput
                        label="Show all columns"
                        checked={showExtraColumns}
                        onChange={() => setShowExtraColumns(prev => !prev)}
                    />
                </>
            }
            beforeTable={!isLoading && showChart && (
                <BillabilityTrendChart
                    trend={trend}
                    trendByGroup={trendByGroup}
                    groupBy={query.groupBy ?? 'departmentLevel'}
                    targets={trendTargets}
                />
            )}
            filters={
                <SearchBillabilityForm
                    query={query}
                    employeeClassOptions={employeeClassOptions}
                    departmentOptions={departmentOptions}
                    branchOptions={branchOptions}
                    groupByOptions={groupByOptions}
                    groupBySelection={groupBySelection(query)}
                    handleChangeGroupBy={handleChangeGroupBy}
                    isSearching={isLoading}
                    handleChangeQuery={handleChangeQuery}
                    handleSearch={handleSearch}
                    handleClearSearch={handleClearSearch}
                />
            }
        />
    )
}

export default BillabilityPage
